import React from 'react';
import pt from 'prop-types';
import Collection from 'containers/Collection';
import StoreIcon from 'components/store/icon';
import KebabButton from 'components/button/kebab';
import Tooltip from 'components/tooltip';
import { getStorefrontURL } from 'utils/storefront';
import { subscriptionTitle } from 'utils/subscription';
import { getPartnerAccessLabel } from 'utils/partner';
import { formatDate } from 'utils';
import './stores.scss';

const { CLIENT_URL, BASE_URI } = process.env;

// Partners are capped at 3 development stores.
const DEV_STORE_LIMIT = 3;

function storeIsDevelopment(partnerClient, partner_id) {
  return (
    partnerClient.status === 'dev' &&
    partnerClient.client?.partner_id === partner_id
  );
}

function storeIsPendingTransfer(partnerClient) {
  return Boolean(
    partnerClient.client?.transfer_invite_code &&
      !partnerClient.client?.owner_id,
  );
}

function getStoreType(partnerClient) {
  switch (partnerClient.status) {
    case 'dev':
      return 'Development';
    case 'connected':
      return 'External';
    default:
      return 'Internal';
  }
}

export default class StoresPage extends React.Component {
  static propTypes = {
    query: pt.object,
    collection: pt.object,
    session: pt.object,
    devStoreCount: pt.number,
    deactivatedStores: pt.object,
    onCreateStore: pt.func,
    onLinkStores: pt.func,
    onDisconnectStore: pt.func,
    onTransferOwnership: pt.func,
    onCancelTransferOwnership: pt.func,
    onDeactivateStore: pt.func,
    onReactivateStore: pt.func,
  };

  static contextTypes = {
    openModal: pt.func.isRequired,
    isOwner: pt.bool,
  };

  fields = {
    name: {
      label: 'Store',
      columns: [
        {
          id: 'icon',
          label: 'Store',
          type: 'image',
          func(partnerClient) {
            return <StoreIcon store={partnerClient.client} size={48} />;
          },
        },
        {
          id: 'name',
          path: 'client.name',
        },
      ],
    },
    id: {
      label: 'ID',
      path: 'client.id',
    },
    plan: {
      label: 'Plan',
      func: (partnerClient) => {
        if (storeIsDevelopment(partnerClient, this.props.session?.partner_id)) {
          if (storeIsPendingTransfer(partnerClient)) {
            return 'Pending Transfer';
          }

          return 'Development store';
        }
        return partnerClient.client?.subscription
          ? subscriptionTitle(partnerClient.client.subscription)
          : null;
      },
    },
    type: {
      label: 'Type',
      func: getStoreType,
    },
    permissions: {
      label: 'Permissions',
      func: (partnerClient) =>
        storeIsDevelopment(partnerClient, this.props.session?.partner_id)
          ? 'Store owner'
          : getPartnerAccessLabel(
              partnerClient,
              partnerClient.advanced_user_permissions,
            ),
    },
    actions: {
      // Sticks to the right edge while the table scrolls
      className: 'action',
      func: (partnerClient) => {
        const storefrontUrl = getStorefrontURL(partnerClient.storefront);
        // Development stores (created from this dashboard) can't be
        // disconnected; ownership of a development store can be handed to one
        // of its users instead.
        const isDevelopmentStore = storeIsDevelopment(
          partnerClient,
          this.props.session?.partner_id,
        );

        const canDisconnect = !isDevelopmentStore;
        const canTransferOwnership = isDevelopmentStore;
        // Only the partner owner can deactivate a development store
        const canDeactivate =
          isDevelopmentStore && Boolean(this.context.isOwner);
        const isPendingTransfer =
          canTransferOwnership && storeIsPendingTransfer(partnerClient);

        return (
          <KebabButton
            side="left"
            anchor="left"
            sideOffset={23}
            alignOffset={13}
            items={[
              storefrontUrl && (
                <button
                  key="button-view-website"
                  data-url={storefrontUrl}
                  onClick={this.onClickViewWebsite}
                >
                  View website
                </button>
              ),
              <button
                key="button-login"
                data-id={partnerClient.client_id}
                onClick={this.onClickLogin}
              >
                Log in to store
              </button>,
              (canDisconnect || canTransferOwnership) && (
                <hr key="separator-1" />
              ),
              canDisconnect && (
                <button
                  key="button-disconnect"
                  className="danger"
                  data-id={partnerClient.client_id}
                  onClick={this.onClickDisconnectStore}
                >
                  Disconnect store
                </button>
              ),
              canTransferOwnership && (
                <button
                  key="button-transfer"
                  data-id={partnerClient.client_id}
                  onClick={this.onClickTransferOwnership}
                >
                  {isPendingTransfer
                    ? 'Resend transfer invite'
                    : 'Transfer ownership'}
                </button>
              ),
              isPendingTransfer && (
                <button
                  key="button-cancel-transfer"
                  className="danger"
                  data-id={partnerClient.client_id}
                  onClick={this.onClickCancelTransferOwnership}
                >
                  Cancel transfer
                </button>
              ),
              canDeactivate && (
                <button
                  key="button-deactivate"
                  className="danger"
                  data-id={partnerClient.client_id}
                  onClick={this.onClickDeactivateStore}
                >
                  Deactivate store
                </button>
              ),
            ]}
            button={false}
          />
        );
      },
    },
  };

  getLoginUrl(clientId) {
    const { session } = this.props;
    const clientUrl = CLIENT_URL.replace('CLIENT_ID', clientId);
    return `${clientUrl}${BASE_URI}/api/user/login-partner/${session.id}`;
  }

  onClickCreateStore = (event) => {
    event.preventDefault();

    const { openModal } = this.context;
    const { onCreateStore, devStoreCount } = this.props;

    openModal('CreateStore', {
      onSubmit: onCreateStore,
      devStoreCount,
      devStoreLimit: DEV_STORE_LIMIT,
    });
  };

  onClickLinkStores = (event) => {
    event.preventDefault();

    const { openModal } = this.context;
    const { onLinkStores } = this.props;

    openModal('LinkStores', {
      onSubmit: onLinkStores,
    });
  };

  onClickViewWebsite = (event) => {
    event.preventDefault();

    const { url } = event.currentTarget.dataset;

    window.open(url, '_blank');
  };

  onClickLogin = (event) => {
    event.preventDefault();

    const { id: clientId } = event.currentTarget.dataset;
    const loginUrl = this.getLoginUrl(clientId);

    window.open(loginUrl, '_blank');
  };

  onClickTransferOwnership = (event) => {
    event.preventDefault();

    const { openModal } = this.context;
    const { collection, onTransferOwnership } = this.props;
    const { id: clientId } = event.currentTarget.dataset;
    const partnerClient = collection?.results?.find(
      (partnerClient) => partnerClient.client_id === clientId,
    );

    if (!partnerClient) {
      return;
    }

    // Ownership can only be transferred for development stores
    const isDevelopmentStore = storeIsDevelopment(
      partnerClient,
      this.props.session?.partner_id,
    );

    if (!isDevelopmentStore) {
      return;
    }

    openModal('TransferStoreOwnership', {
      partnerClient,
      onSubmit: onTransferOwnership,
    });
  };

  onClickCancelTransferOwnership = (event) => {
    event.preventDefault();

    const { openModal } = this.context;
    const { collection, onCancelTransferOwnership } = this.props;
    const { id: clientId } = event.currentTarget.dataset;
    const partnerClient = collection?.results?.find(
      (partnerClient) => partnerClient.client_id === clientId,
    );

    if (!partnerClient) {
      return;
    }

    // Only a development store with a pending transfer can have it cancelled
    const isDevelopmentStore = storeIsDevelopment(
      partnerClient,
      this.props.session?.partner_id,
    );

    if (!isDevelopmentStore || !storeIsPendingTransfer(partnerClient)) {
      return;
    }

    const { id, client } = partnerClient;

    openModal('ConfirmDelete', {
      titleMessage: 'Cancel ownership transfer',
      action: 'Cancel transfer',
      message: (
        <p>
          Are you sure you want to cancel the pending ownership transfer for{' '}
          <b>{client.name}</b>? <br /> The invite link that was sent will stop
          working.
        </p>
      ),
      onConfirm: () => onCancelTransferOwnership(id),
    });
  };

  onClickDeactivateStore = (event) => {
    event.preventDefault();

    const { openModal, isOwner } = this.context;
    const { collection, onDeactivateStore } = this.props;
    const { id: clientId } = event.currentTarget.dataset;
    const partnerClient = collection?.results?.find(
      (partnerClient) => partnerClient.client_id === clientId,
    );

    if (!partnerClient || !isOwner) {
      return;
    }

    // Only development stores can be deactivated
    const isDevelopmentStore = storeIsDevelopment(
      partnerClient,
      this.props.session?.partner_id,
    );

    if (!isDevelopmentStore) {
      return;
    }

    const { id, client } = partnerClient;

    openModal('ConfirmDelete', {
      titleMessage: 'Deactivate store',
      action: 'Deactivate store',
      message: (
        <p>
          Are you sure you want to deactivate <b>{client.name}</b>? <br /> The
          store will stop working and will no longer count towards your
          development store limit. You can reactivate it later.
          {storeIsPendingTransfer(partnerClient) && (
            <>
              <br /> The pending ownership transfer will be cancelled.
            </>
          )}
        </p>
      ),
      onConfirm: () => onDeactivateStore(id),
    });
  };

  onClickReactivateStore = (event) => {
    event.preventDefault();

    const { openModal } = this.context;
    const { deactivatedStores, onReactivateStore } = this.props;
    const { id } = event.currentTarget.dataset;
    const partnerClient = deactivatedStores?.results?.find(
      (partnerClient) => partnerClient.id === id,
    );

    if (!partnerClient) {
      return;
    }

    openModal('Confirm', {
      title: 'Reactivate store',
      action: 'Reactivate store',
      message: (
        <p>
          Are you sure you want to reactivate{' '}
          <b>{partnerClient.client?.name}</b>?
          <br />
          It will count towards your development store limit again.
        </p>
      ),
      onConfirm: () => onReactivateStore(id),
    });
  };

  onClickDisconnectStore = (event) => {
    event.preventDefault();

    const { openModal } = this.context;
    const { collection, onDisconnectStore } = this.props;
    const { id: clientId } = event.currentTarget.dataset;
    const partnerClient = collection?.results?.find(
      (partnerClient) => partnerClient.client_id === clientId,
    );

    if (!partnerClient) {
      return;
    }

    // Development stores can't be disconnected at all
    const isDevelopmentStore = storeIsDevelopment(
      partnerClient,
      this.props.session?.partner_id,
    );

    if (isDevelopmentStore) {
      return;
    }

    const { id, client } = partnerClient;

    openModal('ConfirmDelete', {
      titleMessage: 'Disconnect store',
      action: 'Disconnect store',
      message: (
        <p>
          Are you certain you wish to disconnect the store named{' '}
          <b>{client.name}</b>? This action will not revoke access for users who
          already had access.
        </p>
      ),
      width: 760,
      confirmInput: {
        description: 'Type the name of the store below',
        value: client.name,
      },
      confirmError: `Please enter "${client.name}" to confirm`,
      onConfirm: () => onDisconnectStore(id),
    });
  };

  renderDeactivatedStores(devStoreLimitReached) {
    const { deactivatedStores } = this.props;

    if (!this.context.isOwner || !deactivatedStores?.results?.length) {
      return null;
    }

    return (
      <div className="view-body store-collection-deactivated">
        <h3 className="store-collection-deactivated-title">
          Deactivated stores
        </h3>
        <table className="collection-table outer">
          <thead>
            <tr>
              <th className="image">Store</th>
              <th />
              <th>ID</th>
              <th>Deactivated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {deactivatedStores.results.map((partnerClient) => (
              <tr key={partnerClient.id}>
                <td className="image">
                  <StoreIcon store={partnerClient.client} size={48} />
                </td>
                <td>{partnerClient.client?.name}</td>
                <td>{partnerClient.client_id}</td>
                <td>
                  {partnerClient.client?.date_deactivated ? (
                    formatDate(partnerClient.client.date_deactivated, 'age')
                  ) : (
                    <span className="muted">&mdash;</span>
                  )}
                </td>
                <td className="store-collection-deactivated-actions">
                  <Tooltip
                    side="left"
                    message={
                      devStoreLimitReached
                        ? `Maximum of ${DEV_STORE_LIMIT} development stores reached.`
                        : ''
                    }
                  >
                    <button
                      type="button"
                      className="button button-sm button-secondary"
                      data-id={partnerClient.id}
                      onClick={this.onClickReactivateStore}
                      disabled={devStoreLimitReached}
                    >
                      Reactivate
                    </button>
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const { devStoreCount } = this.props;
    const devStoreLimitReached =
      devStoreCount !== null && devStoreCount >= DEV_STORE_LIMIT;

    return (
      <div className="store-collection">
        <Collection
          title="Stores"
          model="partners:clients"
          endpoint="/partner/clients"
          headerActions={[
            {
              type: 'sub',
              label: 'Connect store',
              onClick: this.onClickLinkStores,
            },
            {
              label: 'New development store',
              onClick: this.onClickCreateStore,
              disabled: devStoreLimitReached,
              title: devStoreLimitReached
                ? `Maximum of ${DEV_STORE_LIMIT} development stores reached.`
                : undefined,
            },
          ]}
          emptyTitle="Create a development store"
          emptyDescription="Development stores have all features of the Advanced plan, with no expiry. They can be used for staging and testing purposes, but do not support live transactions."
          emptyAction="Create development store"
          onClickAction={this.onClickCreateStore}
          emptySecondAction="Link existing store"
          onClickSecondAction={this.onClickLinkStores}
          fields={this.fields}
          selectable={false}
          withPreferences={false}
          summaryDetail={
            devStoreCount !== null
              ? `${devStoreCount} of ${DEV_STORE_LIMIT} development stores used.`
              : undefined
          }
          {...this.props}
        />
        {this.renderDeactivatedStores(devStoreLimitReached)}
      </div>
    );
  }
}
